import { useState, type ReactNode } from "react";
import { QRCode } from "antd";
import { Check, ChevronDown, Copy, KeyRound, ShieldCheck } from "lucide-react";

import { useAuth } from "../../../contexts/Auth";
import { EnrollmentMethod, RECOVERY_COPY, useSecurityEnrollment } from "../../../components/security/useSecurityEnrollment";
import { isPasskeySupported } from "../../../utils/webauthn";
import { Notice, SubmitButton, TextField } from "../../pages/auth/parts";
import { Mono } from "../primitives";
import { Button } from "@/v2/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/v2/ui/collapsible";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/v2/ui/dialog";
import { Field, FieldError, FieldLabel } from "@/v2/ui/field";
import { Input } from "@/v2/ui/input";
import { Skeleton } from "@/v2/ui/skeleton";
import { ReauthenticateDialog } from "./ReauthenticateDialog";

/** Adding an authenticator app (scan, then a code) or a passkey (name it, then the browser's prompt) */
export function EnrollmentDialog({ open, initialMethod, onClose, onComplete }: {
    open: boolean;
    initialMethod?: EnrollmentMethod;
    onClose: () => void;
    onComplete: (factorType: EnrollmentMethod) => void;
}) {
    const flow = useSecurityEnrollment({ open, initialMethod, onComplete });
    const { method, step, error, reauth } = flow;

    let body: ReactNode;
    if (step === 2) {
        body = <Done method={method!} onPasskey={() => flow.choose("passkey")} onClose={onClose} />;
    } else if (method === "passkey") {
        body = <PasskeyStep flow={flow} />;
    } else if (flow.startFailed) {
        body = (
            <Header title="Set up an authenticator app" text="We couldn't start setup. Nothing was changed on your account.">
                <SubmitButton type="button" busy={flow.starting} onClick={() => flow.startTotp()}>Try again</SubmitButton>
            </Header>
        );
    } else {
        body = step === 0 ? <ScanStep flow={flow} /> : <CodeStep flow={flow} error={error} />;
    }

    return (
        <Dialog open={open} onOpenChange={next => !next && onClose()}>
            <DialogContent data-v2="" className="max-h-[calc(100vh-4rem)] overflow-y-auto sm:max-w-md">
                {body}
                {/* Inside the popup, so it stacks as a nested dialog */}
                <ReauthenticateDialog open={reauth.reauthenticating} onCancel={reauth.cancel} onSuccess={reauth.succeed} />
            </DialogContent>
        </Dialog>
    );
}

type Flow = ReturnType<typeof useSecurityEnrollment>;

function Header({ title, text, step, children }: { title: string; text: ReactNode; step?: string; children?: ReactNode }) {
    return (
        <>
            <DialogHeader>
                {step && <span className="text-xs font-medium text-text-3">{step}</span>}
                <DialogTitle className="font-display text-lg font-semibold">{title}</DialogTitle>
                <DialogDescription className="leading-relaxed">{text}</DialogDescription>
            </DialogHeader>
            {children}
        </>
    );
}

function ScanStep({ flow }: { flow: Flow }) {
    const { profile } = useAuth();
    const { totpEnrollment, restarted, totpCountdown } = flow;
    return (
        <Header
            step="Step 1 of 2"
            title="Set up an authenticator app"
            text="Scan this code with an authenticator app such as 1Password, Google Authenticator or Authy."
        >
            {restarted && <Notice tone="info" className="mb-0" title="That setup code expired, so here's a new one. Scan it again." />}
            <div className="flex flex-col items-center gap-3 rounded-2xl bg-soft p-5">
                {/* Dark on white in either theme; inverted codes don't scan in every app */}
                <div className="rounded-xl bg-white p-2">
                    {totpEnrollment?.otpauthUri
                        ? <QRCode value={totpEnrollment.otpauthUri} size={168} type="svg" bordered={false} color="#111111" bgColor="#ffffff" />
                        : <Skeleton className="size-[168px] rounded-lg" />}
                </div>
                {profile?.email && <p className="text-xs text-text-3 [overflow-wrap:anywhere]">{profile.email}</p>}
            </div>
            {totpEnrollment?.secret && <ManualKey secret={totpEnrollment.secret} />}
            <div className="flex flex-col gap-2">
                <Button size="lg" className="h-10 w-full rounded-xl" disabled={!totpEnrollment} onClick={() => { flow.setError(undefined); flow.setStep(1); }}>
                    I've scanned it
                </Button>
                {totpCountdown && <p className="text-center text-xs text-text-3">This code expires in {totpCountdown}</p>}
            </div>
        </Header>
    );
}

function ManualKey({ secret }: { secret: string }) {
    const [copied, setCopied] = useState(false);
    const copy = async () => {
        await navigator.clipboard.writeText(secret);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };
    return (
        <Collapsible>
            <CollapsibleTrigger className="group flex items-center gap-1 text-sm font-medium text-text-2 hover:text-text">
                Can't scan it? Enter a key instead
                <ChevronDown className="size-4 transition-transform group-data-[panel-open]:rotate-180" />
            </CollapsibleTrigger>
            <CollapsibleContent>
                <div className="mt-3 flex items-center gap-2 rounded-xl bg-soft py-2 pr-2 pl-4">
                    {/* Grouped in fours, the way the apps show it, so it's easier to type */}
                    <Mono className="min-w-0 flex-1 text-[13px] tracking-wide break-all text-text">{secret.match(/.{1,4}/g)?.join(" ")}</Mono>
                    <Button variant="ghost" size="icon-sm" onClick={copy} aria-label={copied ? "Copied" : "Copy the key"}>
                        {copied ? <Check /> : <Copy />}
                    </Button>
                </div>
            </CollapsibleContent>
        </Collapsible>
    );
}

function CodeStep({ flow, error }: { flow: Flow; error?: string }) {
    const { code, setCode, setError, verifying, totpCountdown } = flow;
    return (
        <Header step="Step 2 of 2" title="Enter the code" text="Type the six-digit code your authenticator app now shows for Microtica.">
            <form noValidate className="flex flex-col gap-5" onSubmit={e => { e.preventDefault(); flow.verifyTotp(); }}>
                <Field data-invalid={!!error || undefined} className="gap-1.5">
                    <FieldLabel htmlFor="enroll-code" className="sr-only">Six-digit code</FieldLabel>
                    <Input
                        id="enroll-code"
                        autoFocus
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        maxLength={6}
                        aria-invalid={!!error || undefined}
                        value={code}
                        onChange={e => { setCode(e.target.value.replace(/\D/g, "").slice(0, 6)); setError(undefined); }}
                        className="h-12 text-center font-mono text-xl tracking-[0.5em] md:text-xl"
                    />
                    {error && <FieldError className="text-xs text-bad-ink">{error}</FieldError>}
                </Field>
                <div className="flex flex-col gap-2">
                    <SubmitButton busy={verifying} disabled={code.length !== 6}>Turn on two-step verification</SubmitButton>
                    {totpCountdown && <p className="text-center text-xs text-text-3">This code expires in {totpCountdown}</p>}
                </div>
                <button type="button" onClick={() => flow.setStep(0)} className="text-sm font-medium text-text-2 hover:text-text">Back to the QR code</button>
            </form>
        </Header>
    );
}

function PasskeyStep({ flow }: { flow: Flow }) {
    const { passkeyName, setPasskeyName, registering, error } = flow;
    return (
        <Header
            title="Add a passkey"
            text="Your browser will offer Face ID, Touch ID, your device's screen lock, a password manager or a security key. You'll sign in with it instead of a password."
        >
            <form noValidate className="flex flex-col gap-5" onSubmit={e => { e.preventDefault(); flow.registerPasskey(); }}>
                {error && <Notice className="mb-0" title={error} />}
                <TextField
                    label="Name"
                    autoFocus
                    maxLength={80}
                    value={passkeyName}
                    onChange={e => setPasskeyName(e.target.value)}
                />
                <p className="-mt-3 text-xs text-text-3">So you can tell your passkeys apart later.</p>
                <SubmitButton busy={registering}><KeyRound /> Create passkey</SubmitButton>
            </form>
        </Header>
    );
}

function Done({ method, onPasskey, onClose }: { method: EnrollmentMethod; onPasskey: () => void; onClose: () => void }) {
    return (
        <div role="status">
            <span className="mb-5 grid size-12 place-items-center rounded-full bg-ok-tint text-ok-ink"><ShieldCheck className="size-6" /></span>
            <DialogTitle className="font-display text-xl font-semibold">{method === "passkey" ? "Passkey added" : "Two-step verification is on"}</DialogTitle>
            <DialogDescription className="mt-2 leading-relaxed">{RECOVERY_COPY}</DialogDescription>
            <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                {method === "totp" && isPasskeySupported() && <Button variant="outline" size="lg" onClick={onPasskey}>Add a passkey too</Button>}
                <Button size="lg" onClick={onClose}>Done</Button>
            </div>
        </div>
    );
}
